import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, ChevronDown, ChevronLeft, ChevronRight,
  Palette, LayoutDashboard, Database, Users, Layers, Sparkles,
  MessageSquare, UserCheck, ShoppingCart, HelpCircle,
  BarChart3, LineChart, PieChart, Monitor, Globe, Search,
  Instagram, DollarSign, Target, Megaphone, Puzzle, SlidersHorizontal
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const WHATSAPP_URL = 'https://wa.me/5588996527079';

const customizationPoints = [
  {
    icon: Sparkles,
    title: 'Criação do Zero',
    description: 'Dashboard desenhado totalmente do zero, feito sob medida para suas métricas, fontes de dados e preferências visuais.'
  },
  {
    icon: Puzzle,
    title: 'Adaptação de Templates',
    description: 'Tenho modelos próprios ou posso adaptar os seus ou de terceiros, adicionando sua identidade visual e métricas essenciais.'
  },
  {
    icon: Megaphone,
    title: 'Multi Plataformas',
    description: 'Meta Ads, Google Ads, GA4 e várias outras fontes, como Instagram Insights e Google Search Console.'
  },
  {
    icon: Target,
    title: 'Vários Objetivos',
    description: 'Modelos adaptados para campanhas de conversão, mensagens, leads, vendas e outras que desejar.'
  }
];

const dataSources = [
  { icon: BarChart3, name: 'Meta Ads', desc: 'Facebook e Instagram Ads' },
  { icon: LineChart, name: 'Google Ads', desc: 'Search, Display e YouTube' },
  { icon: Monitor, name: 'TikTok Ads', desc: 'Campanhas no TikTok' },
  { icon: PieChart, name: 'GA4', desc: 'Google Analytics 4' },
  { icon: Search, name: 'Search Console', desc: 'Dados de pesquisa orgânica' },
  { icon: Instagram, name: 'Instagram Insights', desc: 'Métricas do perfil' }
];

const pricingFactors = [
  {
    icon: Database,
    title: 'Fontes de Dados',
    description: 'Quantas plataformas serão integradas (Meta, Google, GA4, etc.). Mais fontes = mais conectores e configuração.'
  },
  {
    icon: SlidersHorizontal,
    title: 'Complexidade das Campanhas',
    description: 'Número de contas de anúncios, volume de campanhas e nível de segmentação que precisa ser refletido.'
  },
  {
    icon: Palette,
    title: 'Nível de Customização',
    description: 'Do layout básico ao totalmente personalizado com identidade visual completa, animações e páginas extras.'
  }
];

const faqs = [
  {
    question: 'Os dashboards atualizam automaticamente?',
    answer: 'Sim. Os dados de Meta Ads, Google Ads e outras fontes são conectados diretamente ao Looker Studio via conectores, sem necessidade de atualização manual.'
  },
  {
    question: 'Consigo replicar o mesmo dashboard para vários clientes?',
    answer: 'Sim! Posso criar um modelo mestre que é facilmente replicável, seja mantendo a identidade visual da sua agência ou usando as cores de cada cliente.'
  },
  {
    question: 'Preciso pagar algum conector ou ferramenta extra?',
    answer: 'Apenas do Google Ads e GA4 serem gratuitos, para o Meta Ads e demais fontes específicas é preciso conectors pagos, mas que custam em média apenas R$ 100,00 a R$ 150,00 por mês.'
  },
  {
    question: 'Quanto tempo leva para ficar pronto?',
    answer: 'Entre 1 e 2 semanas, dependendo da quantidade de fontes e nível de personalização. Dashboards mais simples podem ser entregues em poucos dias.'
  },
  {
    question: 'E se eu precisar de ajustes depois da entrega?',
    answer: 'Você conta com suporte contínuo mesmo após a conclusão. Ajustes e melhorias pontuais podem ser incorporados a qualquer momento.'
  }
];

const agencyLogos = [
  'Keep Ads', 'Raro Digital', 'Kaxu Digital', 'Hawk Digital',
  'A Case Aceleradora', 'Agência Budiga', 'Gooxie Group', 'Giraffe Marketing',
  'Agência Meio'
];

const showcaseImages = [
  '/assets/DashGoogleAds.jpeg',
  '/assets/DashMetaAds.jpeg',
  '/assets/DashGA4Ecommerce.jpeg',
  '/assets/DashGoogle+MetaAds.jpeg'
];

const DashboardsTrafegoPagoLanding = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const nextImage = () => setCurrentImage((p) => (p + 1) % showcaseImages.length);
  const prevImage = () => setCurrentImage((p) => (p - 1 + showcaseImages.length) % showcaseImages.length);

  return (
    <>
      <Helmet>
        <title>Dashboards de Tráfego Pago Profissionais | Looker Studio</title>
        <meta name="description" content="Dashboards customizados para Google e Meta Ads. Visualize ROI, CPA e performance de campanhas em tempo real." />
      </Helmet>
    <div className="page-dt-landing">

      {/* ===== HERO TRÁFEGO PAGO ===== */}
<section className="dt-hero" data-testid="dt-hero" style={{ overflow: 'hidden', padding: '40px 0' }}>
  <div className="rl-container"> {/* Container largo padronizado */}
    <Link to="/services" className="back-link dt-back-link" data-testid="dt-back-link" style={{ marginBottom: '20px', display: 'inline-flex' }}>
      <ArrowLeft size={18} />
      Voltar aos Serviços
    </Link>

    <div className="bv-hero-grid" style={{ 
      display: 'grid', 
      gridTemplateColumns: '1.4fr 0.6fr', 
      gap: '60px', 
      alignItems: 'center', 
      marginTop: '40px' 
    }}>
      <div className="dt-hero-content">
        <div className="dt-hero-badge">
          <LayoutDashboard size={14} />
          Tráfego Pago e Orgânico
        </div>
        <h1 className="dt-hero-title" style={{ marginTop: '15px', fontSize: '2.4rem', lineHeight: '1.2' }}>
          Dashs de Tráfego que <span className="dt-accent">valorizam seu trabalho</span> e economizam seu tempo
        </h1>
        <p className="dt-hero-subtitle" style={{ marginTop: '20px', marginBottom: '30px', fontSize: '1.1rem' }}>
          Relatórios personalizados do zero integrando Meta Ads, Google Ads, GA4 e mais.
          Identidade visual própria para cada cliente seu.
        </p>
        <div className="dt-hero-cta-group">
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="dt-btn-primary btn-large" data-testid="dt-hero-cta">
            Solicitar Orçamento Sob Medida
            <ArrowRight size={20} />
          </a>
        </div>
      </div>

      <div className="dt-hero-visual hide-on-mobile" aria-hidden="true" style={{ display: 'flex', justifyContent: 'center' }}>
        {/* Grid de KPIs simulando um dashboard real */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '12px', 
          width: '100%', 
          maxWidth: '350px' 
        }}>
          {/* Gráfico de Barras Mini */}
          <div style={{ gridColumn: 'span 2', backgroundColor: '#1a1a1a', padding: '15px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', height: '100px' }}>
             {[45, 72, 95, 60, 80].map((h, i) => (
               <div key={i} style={{ width: '15%', height: `${h}%`, backgroundColor: i === 2 ? '#F2D230' : '#F2D230', borderRadius: '4px' }} />
             ))}
          </div>
          {/* Cards de KPI */}
          {[
            { label: 'CPC', val: 'R$ 1,42' },
            { label: 'CTR', val: '3,8%' },
            { label: 'CUSTO/RESULTADO', val: 'R$ 5,2'},
            { label: 'CONVERSÃO', val: '2,1%' }
          ].map((kpi, i) => (
            <div key={i} style={{ backgroundColor: '#1a1a1a', padding: '15px', borderRadius: '12px', border: kpi.high ? '1px solid #00cc66' : '1px solid rgba(255,255,255,0.1)', textAlign: 'center' }}>
              <span style={{ fontSize: '10px', opacity: 0.6, display: 'block', marginBottom: '5px' }}>{kpi.label}</span>
              <span style={{ fontSize: '18px', fontWeight: 'bold', color: kpi.high ? '#00cc66' : '#fff' }}>{kpi.val}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
</section>

      {/* ===== PERSONALIZAÇÃO ===== */}
      <section className="dt-customization" data-testid="dt-customization">
        <div className="dt-container">
          <div className="dt-section-header">
            <h2 className="dt-section-title">Tudo 100% Personalizável</h2>
            <p className="dt-section-subtitle">
              Cada dashboard é pensado para o objetivo específico do seu cliente ou da sua operação
            </p>
          </div>
          <div className="dt-custom-grid" style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: '24px',
          width: '100%'
}}>
          {customizationPoints.map((item, i) => {
           const Icon = item.icon;
          return (
          <div key={i} className="dt-custom-card" data-testid={`dt-custom-card-${i}`}>
          <div className="dt-custom-icon">
          <Icon size={30} />
        </div>
        <h3 className="dt-custom-title">{item.title}</h3>
        <p className="dt-custom-text">{item.description}</p>
      </div>
    );
  })}
</div>
        </div>
      </section>

      {/* ===== ECOSSISTEMA DE DADOS ===== */}
      <section className="dt-datasources" data-testid="dt-datasources">
        <div className="dt-container">
          <div className="dt-section-header">
            <h2 className="dt-section-title">Ecossistema de Tráfego Integrado</h2>
            <p className="dt-section-subtitle">
              Todas as fontes de dados que podem alimentar seus dashboards
            </p>
          </div>
          <div className="dt-sources-grid">
            {dataSources.map((src, i) => {
              const Icon = src.icon;
              return (
                <div key={i} className="dt-source-card" data-testid={`dt-source-${i}`}>
                  <div className="dt-source-icon">
                    <Icon size={24} />
                  </div>
                  <span className="dt-source-name">{src.name}</span>
                  <span className="dt-source-desc">{src.desc}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ===== ESCALA PARA AGÊNCIAS ===== */}
      <section className="dt-scale" data-testid="dt-scale">
        <div className="dt-container">
          <div className="dt-scale-grid">
            <div className="dt-scale-content">
              <h2 className="dt-section-title" style={{ textAlign: 'left' }}>Feito para Escalar com Sua Agência</h2>
              <p className="dt-scale-text">
                Crie um dashboard mestre para múltiplas contas de anúncios ou relatórios individuais
                com a identidade visual de cada cliente. A solução cresce junto com a sua operação.
              </p>
              <ul className="dt-scale-list">
                <li>
                  <Layers size={18} />
                  Dashboard mestre com visão consolidada de todos os clientes
                </li>
                <li>
                  <Users size={18} />
                  Relatórios individuais com logo e cores de cada cliente
                </li>
                <li>
                  <Globe size={18} />
                  Replicável para novos clientes em poucos cliques
                </li>
              </ul>
            </div>
            <div className="dt-scale-visual" aria-hidden="true">
              <div className="dt-scale-cards-stack">
                <div className="dt-stack-card dt-stack-3">
                  <div className="dt-stack-dot" style={{ background: '#F59E0B' }} />
                  <span>Cliente C</span>
                </div>
                <div className="dt-stack-card dt-stack-2">
                  <div className="dt-stack-dot" style={{ background: '#3B82F6' }} />
                  <span>Cliente B</span>
                </div>
                <div className="dt-stack-card dt-stack-1">
                  <div className="dt-stack-dot" style={{ background: '#8B5CF6' }} />
                  <span>Cliente A</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PRECIFICAÇÃO ===== */}
      <section className="dt-pricing" data-testid="dt-pricing">
        <div className="dt-container">
          <div className="dt-section-header">
            <h2 className="dt-section-title">Como Funciona a Precificação</h2>
            <p className="dt-section-subtitle">
              Valor super justo e transparente, calculado com base no escopo real
            </p>
          </div>
          <div className="dt-pricing-grid">
            {pricingFactors.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="dt-pricing-card" data-testid={`dt-pricing-card-${i}`}>
                  <div className="dt-pricing-number">{String(i + 1).padStart(2, '0')}</div>
                  <div className="dt-pricing-icon">
                    <Icon size={28} />
                  </div>
                  <h3 className="dt-pricing-title">{item.title}</h3>
                  <p className="dt-pricing-text">{item.description}</p>
                </div>
              );
            })}
          </div>
          <div className="dt-pricing-note">
            <DollarSign size={20} />
            <p>
              Após entender seu escopo, envio um orçamento detalhado sem compromisso.
              Faixa geral: <strong>R$ 200 a R$ 1.000</strong>, dependendo da complexidade.
            </p>
          </div>
        </div>
      </section>

      {/* ===== PROVA SOCIAL / LOGOS ===== */}
      <section className="dt-social-proof" data-testid="dt-social-proof">
        <div className="dt-container">
          <div className="dt-section-header">
            <h2 className="dt-section-title">Clientes e Agências que Já Confiaram</h2>
          </div>
        </div>
        <div className="dt-logos-track-wrapper">
          <div className="dt-logos-track">
            {[...agencyLogos, ...agencyLogos].map((name, i) => (
              <div key={i} className="dt-logo-item" data-testid={`dt-logo-${i}`}>
                <span>{name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CARROSSEL ===== */}
      <section className="dt-showcase" data-testid="dt-showcase">
        <div className="dt-container">
          <div className="dt-section-header">
            <h2 className="dt-section-title">Exemplos dos Dashboards</h2>
            <p className="dt-section-subtitle">
              Veja alguns modelos de dashboards já entregues para clientes reais
            </p>
          </div>
          <div className="portfolio-detail-container">
            <div className="portfolio-image-carousel">
              <img
                src={showcaseImages[currentImage]}
                alt={`Dashboard exemplo ${currentImage + 1}`}
                className="portfolio-carousel-image"
              />
              {showcaseImages.length > 1 && (
                <>
                  <button className="carousel-btn carousel-btn-prev" onClick={prevImage} aria-label="Imagem anterior" data-testid="dt-carousel-prev">
                    <ChevronLeft size={24} />
                  </button>
                  <button className="carousel-btn carousel-btn-next" onClick={nextImage} aria-label="Próxima imagem" data-testid="dt-carousel-next">
                    <ChevronRight size={24} />
                  </button>
                  <div className="carousel-indicators">
                    {showcaseImages.map((_, index) => (
                      <button
                        key={index}
                        className={`carousel-indicator ${index === currentImage ? 'active' : ''}`}
                        onClick={() => setCurrentImage(index)}
                        aria-label={`Ir para imagem ${index + 1}`}
                      />
                    ))}
                  </div>
                  <div className="carousel-counter">
                    {currentImage + 1} / {showcaseImages.length}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="dt-faq" data-testid="dt-faq">
        <div className="dt-container dt-faq-container">
          <div className="dt-section-header">
            <div className="dt-faq-icon-wrapper">
              <HelpCircle size={32} />
            </div>
            <h2 className="dt-section-title">Perguntas Frequentes</h2>
          </div>
          <div className="faq-grid-modern">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`faq-card-modern ${openFaq === index ? 'faq-card-open' : ''}`}
              >
                <button
                  className="faq-question-modern"
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  data-testid={`dt-faq-question-${index}`}
                >
                  <span className="faq-number dt-faq-number">{String(index + 1).padStart(2, '0')}</span>
                  <span className="faq-question-text">{faq.question}</span>
                  <ChevronDown
                    size={20}
                    className={`faq-chevron ${openFaq === index ? 'faq-chevron-open' : ''}`}
                  />
                </button>
                <div className={`faq-answer-modern ${openFaq === index ? 'faq-answer-visible' : ''}`}>
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA FINAL ===== */}
      <section className="dt-final-cta" data-testid="dt-final-cta">
        <div className="dt-container">
          <div className="dt-final-cta-content">
            <h2 className="dt-final-cta-title">Vamos Criar o(s) Dashboard(s) Ideal(is) Para Seus Clientes?</h2>
            <p className="dt-final-cta-text">
              Venha me contar mais sobre sua operação e receber um orçamento personalizado, sem compromisso.
            </p>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="dt-btn-primary" data-testid="dt-final-cta-btn">
              Solicitar Orçamento Sob Medida
              <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default DashboardsTrafegoPagoLanding;
